import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi } from '@/api/tasks';
import type { TaskListItem, DeliveryStatus } from '@/types';
import { BottomNav } from '@/components/BottomNav';
import { showBrowserNotification } from '@/utils/browserNotification';

const TASKS_POLL_INTERVAL = 15_000;

type TabKey = 'assigned' | 'active' | 'completed';

const statusForTab: Record<TabKey, DeliveryStatus[]> = {
  assigned: ['CREATED', 'DRIVER_ASSIGNED'],
  active: ['PICKUP_ARRIVED', 'PICKED_UP', 'OUT_FOR_DELIVERY'],
  completed: ['DELIVERED', 'CANCELLED', 'FAILED'],
};

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
}

function statusBadgeClass(status: DeliveryStatus): string {
  switch (status) {
    case 'CREATED':
      return 'badge-created';
    case 'DRIVER_ASSIGNED':
    case 'PICKUP_ARRIVED':
      return 'badge-assigned';
    case 'PICKED_UP':
    case 'OUT_FOR_DELIVERY':
      return 'badge-picked';
    case 'DELIVERED':
      return 'badge-delivered';
    default:
      return 'badge-cancelled';
  }
}

export function TasksPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('assigned');
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const prevAssignedCountRef = useRef<number | null>(null);

  const fetchTasks = useCallback(async () => {
    setError('');
    try {
      const res = await tasksApi.getTasks();
      if (res.data) {
        setTasks(res.data.tasks);
        // Initialize the count ref on first fetch so polling doesn't false-trigger
        if (prevAssignedCountRef.current === null) {
          prevAssignedCountRef.current = res.data.tasks.filter((t: TaskListItem) =>
            statusForTab.assigned.includes(t.status)
          ).length;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Poll for task updates every 15 seconds
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    pollRef.current = setInterval(() => {
      tasksApi.getTasks().then((res) => {
        if (res.data) {
          const newTasks = res.data.tasks;
          const assignedCount = newTasks.filter((t: TaskListItem) =>
            statusForTab.assigned.includes(t.status)
          ).length;
          if (prevAssignedCountRef.current !== null && assignedCount > prevAssignedCountRef.current) {
            const diff = assignedCount - prevAssignedCountRef.current;
            showBrowserNotification(
              'New Delivery Task',
              `${diff} new task${diff > 1 ? 's' : ''} assigned!`
            );
            try { new Audio('/notification.mp3').play().catch(() => {}); } catch {}
          }
          prevAssignedCountRef.current = assignedCount;
          setTasks(newTasks);
        }
      }).catch(() => {});
    }, TASKS_POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Refetch when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchTasks();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchTasks]);

  const filtered = tasks.filter((t) => statusForTab[tab].includes(t.status));

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'assigned', label: 'Assigned' },
    { key: 'active', label: 'Active' },
    { key: 'completed', label: 'Completed' },
  ];

  return (
    <div style={{ paddingBottom: 72 }}>
      <div className="container">
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16, paddingTop: 8 }}>Tasks</h1>

        <div className="tabs" style={{ marginBottom: 16 }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`tab ${tab === t.key ? 'tab-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {tasks.filter((tk) => statusForTab[t.key].includes(tk.status)).length > 0 && (
                <span style={{ marginLeft: 4, fontSize: 12, opacity: 0.7 }}>
                  ({tasks.filter((tk) => statusForTab[t.key].includes(tk.status)).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="loading-center">
            <div className="spinner" />
          </div>
        )}

        {error && <div className="error-state">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            <p style={{ fontSize: 36 }}>
              {tab === 'assigned' ? '📋' : tab === 'active' ? '🚀' : '✅'}
            </p>
            <p>No {tab} tasks</p>
          </div>
        )}

        {!loading &&
          filtered.map((task) => (
            <div
              key={`${task.type}-${task.id}`}
              className="card"
              style={{ marginBottom: 10, cursor: 'pointer' }}
              onClick={() => navigate(`/tasks/${task.type.toLowerCase()}/${task.id}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>{task.type === 'FOOD' ? '🍔' : '👕'}</span>
                    <p style={{ fontWeight: 600, fontSize: 15 }}>{task.vendorName}</p>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    {task.dropArea || task.dropAddress}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {formatTime(task.createdAt)}
                  </p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span className={`badge ${statusBadgeClass(task.status)}`}>
                    {task.status.replace(/_/g, ' ')}
                  </span>
                  <div style={{ marginTop: 8 }}>
                    {task.paymentType === 'COD' ? (
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warning)' }}>
                        COD ₹{task.codAmount}
                      </span>
                    ) : (
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
                        PREPAID
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      <BottomNav />
    </div>
  );
}
